import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import BarChart from "./Visuals/BarChart";
import { Category, DataPoint, ProviderFile } from "fileParsing/types";
import { FC, ReactNode } from "react";
import TinyTimeline from "./Visuals/TinyTimeline";
import { PictureExtensions } from "constants/filenames";
import { useProviderFiles } from "containers/hooks/useData";
import WorldMap from "../Components/WorldMap";
import { StationaryTooltip } from "containers/Components/Tooltip";
import { toISO8601Date } from "@utils/format";
import { useResizeObserver } from "containers/hooks/useResizeObserver";

type PreviewCardProps = {
  title: string;
  children?: ReactNode;
};
const PreviewCard: FC<PreviewCardProps> = ({ title, children }) => {
  return (
    <Paper sx={{ m: 1, p: 1, overflow: "hidden" }}>
      <Box>
        <Typography variant="caption">{title}</Typography>
      </Box>
      {children}
    </Paper>
  );
};

const CategoryPreview: FC<{
  data: DataPoint[];
}> = ({ data }) => {
  const theme = useTheme();
  const getBarChartData = () => {
    const result = Object.values(Category).map((category) => {
      const items = data.reduce((acc1, val1) => acc1 + (val1.category === category ? 1 : 0), 0);

      return {
        key: category,
        color: theme.palette.categories[category].main,
        value: items,
      };
    });
    return result;
  };
  return <BarChart data={getBarChartData()} size={{ width: "100%", height: 200 }} />;
};

const TitlePreview: FC<{ data: DataPoint[] }> = ({ data }) => {
  const titles = Object.entries(
    data.reduce<Record<string, number>>((result, { subcategory }) => {
      if (subcategory in result)
        return Object.assign(result, {
          [subcategory]: result[subcategory] + 1,
        });

      return Object.assign(result, { [subcategory]: 1 });
    }, {}),
  ).sort(([, a], [, b]) => b - a);

  const list = titles.slice(0, 4).map(([title, count], ix) => (
    <li key={ix}>
      {title}
      {count > 1 ? ` (${count})` : null}
    </li>
  ));
  return (
    <ul>
      {list}
      {titles.length > list.length ? <li>... and {titles.length - list.length} more.</li> : null}
    </ul>
  );
};

const TimePreview: FC<{ data: DataPoint[] }> = ({ data }) => {
  return <TinyTimeline data={data} size={{ width: "100%", height: 80 }} />;
};

const URIPreview: FC<{ data: DataPoint[] }> = ({ data }) => {
  const providerFiles = useProviderFiles();
  const getFile = (uri: string) => providerFiles.find(({ path }) => path === uri);
  const pictures = data
    .filter(({ uri }) => uri !== undefined && PictureExtensions.some((ext) => uri.endsWith(ext)))
    .slice(0, 5)
    .map(({ uri }) => {
      return { binary: getFile(uri ?? String())?.binaryData, uri };
    })
    .filter(({ binary }) => binary !== null && binary !== undefined);

  return (
    <Stack direction="row" overflow="hidden" flexWrap="wrap" maxHeight="100px">
      {pictures.map((uri, i) => (
        <a key={i} href={uri.binary as string}>
          <img src={uri.binary as string} alt={uri.uri} height="50" />
        </a>
      ))}
    </Stack>
  );
};

const LocationPreview: FC<{
  data: DataPoint[];
}> = ({ data }) => {
  return <WorldMap data={data} size={{ width: "100%", height: "100%" }} />;
};

type DataPreviewProps = {
  list: DataPoint[];
};

function DataPointPreview(props: DataPreviewProps) {
  const [ref, size] = useResizeObserver();
  if (props.list.length > 0) {
    const data = props.list[0];
    return (
      <div ref={ref}>
        <StationaryTooltip
          settings={{ tooltipWidth: size?.width || 100, margin: 0 }}
          visible={true}
          texts={{
            title: data.category,
            subtitle: data.subcategory,
            date: toISO8601Date(data.date),
            body: data.text,
          }}
          category={data.category}
          uri={data.uri}
          isFixed={false}
        />
      </div>
    );
  } else {
    return <></>;
  }
}

const Preview: FC<{ files: ProviderFile[] }> = ({ files }) => {
  const data = files.reduce<DataPoint[]>((data, file) => data.concat(file.data), []);
  return (
    <>
      <Typography variant="h5">Preview</Typography>

      <PreviewCard title="Category">
        <CategoryPreview data={data} />
      </PreviewCard>

      <PreviewCard title="Title">
        <TitlePreview data={data} />
      </PreviewCard>

      <PreviewCard title="Timestamps">
        <TimePreview data={data} />
      </PreviewCard>

      <PreviewCard title="URI">
        <URIPreview data={data} />
      </PreviewCard>

      <PreviewCard title="Location">
        <LocationPreview data={data} />
      </PreviewCard>

      <PreviewCard title="Data Point">
        <DataPointPreview list={data} />
      </PreviewCard>
    </>
  );
};

export default Preview;
